import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, MaterialIcon } from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import enhanceWithCohort from 'hocs/enhanceWithCohort';

import {
  PageWrapper,
  PageViewport,
  PageContent,
  PageActions,
  PageActionsRight,
} from 'components/PageStyles';
import Modal from 'components/Modal';

import Sidebar from 'Workspace/components/WorkspaceSidebar';
import cohortShape from 'Workspace/types/cohorts/cohort';
import LockPluginVersionModal from 'Workspace/Cohorts/components/DeployCohortCoursewareModal/LockPluginVersionModal';

import hardCodedWorkspaceConfig from 'Workspace/Cohorts/hardcoded-config-by-workspace.json';
import gtmService from 'utils/gtm/gtmService';
import workspaceService from 'services/WorkspaceService';
import { selectWorkspacesLoadingState } from 'Workspace/Admin/store/selectors';

import CohortPageHeader from '../CohortPageHeader';
import CohortNavigation from '../CohortNavigation';
import DeployCohortCoursewareModal from '../DeployCohortCoursewareModal';

import CohortDeploymentsList from '../CohortDeploymentsList';
import messages from './messages';
import EnrollmentLinksModal from '../EnrollmentLinksModal';
import { pageName } from 'utils/gtm/constants';
import { commandRevokePermissionOnCohort,
} from 'containers/ConnectionManager/actions';


class CohortCourseware extends React.Component {
  static propTypes = {
    cohort: cohortShape,
    getDeployments: PropTypes.func,
    updateCohort: PropTypes.func,
    deploy: PropTypes.func,
    updateDeployment: PropTypes.func,
    workspacesLoading: PropTypes.bool,
    revokePermission: PropTypes.bool,
  }

  state = {
    isCohortCoursewareModalShown: false,
    selectedDeploymentId: undefined,
    isUpdateModalShown: false,
    deploymentState: {},
  };

  componentDidMount() {
    const { cohort, getDeployments } = this.props;

    getDeployments(cohort.summary.cohortId);
    gtmService.pushTelemetryPageActivityAndView({ pageName: pageName.COHORT_COURSEWARE_PAGE });
  }

  componentDidUpdate() {
    const { workspacesLoading, cohort } = this.props;
    if (!workspacesLoading) {
      workspaceService.updateWorkspace(cohort.summary.workspaceId);
    }
  }

  onPublishActivity = () => {
    this.hideCohortCoursewareModal();
  };

  onDeploymentUpdate = (activityId, cohortId, deploymentId, checkboxValues) => {
    const { lockPluginVersion, supportOnDemandEnabled, scorable, forceUpdateAssessments } = checkboxValues;

    this.setUpdateModal(false);
    this.props.updateDeployment(activityId, cohortId, deploymentId, lockPluginVersion, supportOnDemandEnabled, scorable, forceUpdateAssessments);
  }

  setUpdateModal = (val) => this.setState({ isUpdateModalShown: val })

  showCohortCoursewareModal = () => this.setState({ isCohortCoursewareModalShown: true });
  hideCohortCoursewareModal = () => this.setState({ isCohortCoursewareModalShown: false });

  showEnrollmentLinksModal = (deploymentId) => this.setState({ selectedDeploymentId: deploymentId });
  hideEnrollmentLinksModal = () => this.setState({ selectedDeploymentId: undefined });

  updateDeploymentState = (val) => {
    this.setUpdateModal(true);
    this.setState({ deploymentState: val });
  }

  render() {
    const {
      cohort,
      updateCohort,
      revokePermission,
    } = this.props;
    const { isCohortCoursewareModalShown, selectedDeploymentId, isUpdateModalShown, deploymentState } = this.state;
    const { activityId, cohortId, deploymentId, deploymentDetails } = deploymentState;
    const hasDeployments = !!(cohort && cohort.deployments && cohort.deployments.length);
    const workspaceConfig = cohort && cohort.summary && hardCodedWorkspaceConfig[cohort.summary.workspaceId];
    const hasEnrollmentLinks = !!(workspaceConfig &&
      (workspaceConfig.OIDC_RELYING_PARTY_ID || workspaceConfig.LTI_CONSUMER_KEY));

    return (
      <PageWrapper>
        <DeployCohortCoursewareModal
          cohortId={cohort.summary.cohortId}
          onPublishActivity={this.onPublishActivity}
          deploy={this.props.deploy}
          onDismiss={this.hideCohortCoursewareModal}
          visible={isCohortCoursewareModalShown}
        />

        { hasEnrollmentLinks && selectedDeploymentId && (
          <EnrollmentLinksModal
            ssoId={workspaceConfig.OIDC_RELYING_PARTY_ID}
            ltiConsumerKey={workspaceConfig.LTI_CONSUMER_KEY}
            ltiConsumerSecret={workspaceConfig.LTI_CONSUMER_SECRET}
            cohortId={cohort.summary.cohortId}
            deploymentId={selectedDeploymentId}
            onDismiss={this.hideEnrollmentLinksModal}
            visible={!!selectedDeploymentId}
          />
        )}

        <Sidebar navigation={<CohortNavigation cohortId={cohort.summary.cohortId} selectedKey="courseware" />} />
        <PageViewport>
          {cohort && cohort.summary &&
            <Fragment>
              <CohortPageHeader
                cohort={cohort}
                title={intl.formatMessage(messages.title)}
                updateCohort={updateCohort}
                onRevoke={(account)=>{revokePermission(account, cohort.summary.cohortId);}}
              />
              <PageActions>
                <PageActionsRight>
                  <Button primary onClick={this.showCohortCoursewareModal}>
                    <MaterialIcon id="add" left />
                    {intl.formatMessage(messages.newCouseware)}
                  </Button>
                </PageActionsRight>
              </PageActions>
              <PageContent>
                {hasDeployments && (
                  <CohortDeploymentsList
                    deployments={cohort.deployments}
                    cohortId={cohort.summary.cohortId}
                    updateParentDeploymentState={this.updateDeploymentState}
                    hasEnrollmentLinks={hasEnrollmentLinks}
                    onShowEnrollmentLinks={this.showEnrollmentLinksModal}
                  />
                )}
                {isUpdateModalShown && <Modal
                  visible
                  title={intl.formatMessage(messages.coursewareSettings)}
                  onDismiss={() => this.setUpdateModal(false)}
                >
                  <LockPluginVersionModal
                    onDismiss={() => this.setUpdateModal(false)}
                    onDeploy={(checkboxValues) => this.onDeploymentUpdate(activityId, cohortId, deploymentId, checkboxValues)}
                    cohortId={cohortId}
                    deploymentId={deploymentId}
                    deploymentDetails={deploymentDetails}
                  />)
                </Modal>}
              </PageContent>
            </Fragment>
          }
        </PageViewport>
      </PageWrapper>
    );
  }
}

const withCohort = enhanceWithCohort(({ match }) => match.params.cohortId);

const mapStateToProps = (state) => ({
  workspacesLoading: selectWorkspacesLoadingState(state),
});

const mapDispatchToProps = (dispatch) => ({
  revokePermission: (account, cohortId) => dispatch(commandRevokePermissionOnCohort(account.id, account.type, cohortId)),
});

export { CohortCourseware };
export default connect(mapStateToProps, mapDispatchToProps)(withCohort(CohortCourseware));

